import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
} from '@mui/material';
import { createInvitation } from '../../api/admin';
import { ApiError } from '../../api/client';
import type { InvitableRole, InvitationResponse } from '../../api/types';
import ErrorAlert from '../ErrorAlert';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface UserInviteDialogProps {
  open: boolean;
  orgSlug: string;
  /** False when the deployment cannot send mail — the dialog says so up front. */
  mailEnabled: boolean;
  onSuccess: (invitation: InvitationResponse) => void;
  onClose: () => void;
}

/**
 * Spec 045 — invite a colleague by address and role.
 *
 * Deliberately shorter than the "create user" dialog next to it: there is no
 * password field, because the whole point is that the administrator never
 * chooses, sees, or has to transmit somebody else's password.
 */
const UserInviteDialog: React.FC<UserInviteDialogProps> = ({
  open,
  orgSlug,
  mailEnabled,
  onSuccess,
  onClose,
}) => {
  const { t, i18n } = useTranslation(['admin', 'auth', 'common']);
  const fullScreen = useMediaQuery('(max-width:599px)');

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<InvitableRole>('coordinator');
  const [language, setLanguage] = useState<'de' | 'en'>(
    (i18n.resolvedLanguage === 'en' ? 'en' : 'de'),
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setEmail('');
    setDisplayName('');
    setRole('coordinator');
    setFieldErrors({});
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!email.trim()) {
      errors.email = t('auth:register.errors.required');
    } else if (!EMAIL_RE.test(email.trim())) {
      errors.email = t('auth:register.errors.emailInvalid');
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!validate()) return;
    setSubmitting(true);
    try {
      const invitation = await createInvitation(orgSlug, {
        email: email.trim(),
        role,
        language,
        ...(displayName.trim() ? { display_name: displayName.trim() } : {}),
      });
      resetForm();
      onSuccess(invitation);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <form onSubmit={handleSubmit} noValidate>
        <DialogTitle>{t('admin:users.invite.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ marginBlockEnd: 2 }}>
            {t('admin:users.invite.intro')}
          </DialogContentText>

          {!mailEnabled && (
            <Alert severity="warning" sx={{ marginBlockEnd: 2 }}>
              {t('admin:users.invite.mailDisabled')}
            </Alert>
          )}

          {error && <ErrorAlert message={error} />}

          <TextField
            autoFocus
            fullWidth
            margin="dense"
            type="email"
            label={t('admin:users.invite.emailLabel')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!fieldErrors.email}
            helperText={fieldErrors.email}
            disabled={submitting}
            required
            autoComplete="email"
          />

          <TextField
            fullWidth
            margin="dense"
            label={t('admin:users.invite.displayNameLabel')}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            helperText={t('admin:users.invite.displayNameHelper')}
            disabled={submitting}
          />

          <FormControl fullWidth margin="dense">
            <InputLabel id="invite-role-label">{t('admin:users.invite.roleLabel')}</InputLabel>
            <Select
              labelId="invite-role-label"
              label={t('admin:users.invite.roleLabel')}
              value={role}
              onChange={(e) => setRole(e.target.value as InvitableRole)}
              disabled={submitting}
            >
              <MenuItem value="participant">{t('admin:users.roles.participant')}</MenuItem>
              <MenuItem value="coordinator">{t('admin:users.roles.coordinator')}</MenuItem>
              <MenuItem value="creator">{t('admin:users.roles.creator')}</MenuItem>
              <MenuItem value="org_admin">{t('admin:users.roles.org_admin')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="dense">
            <InputLabel id="invite-language-label">
              {t('admin:users.invite.languageLabel')}
            </InputLabel>
            <Select
              labelId="invite-language-label"
              label={t('admin:users.invite.languageLabel')}
              value={language}
              onChange={(e) => setLanguage(e.target.value as 'de' | 'en')}
              disabled={submitting}
            >
              <MenuItem value="de">Deutsch</MenuItem>
              <MenuItem value="en">English</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : t('admin:users.invite.submitButton')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserInviteDialog;
